// src/.../BottomSheet.jsx

import React, { useLayoutEffect, useRef, useCallback } from "react";
import ReactDOM from "react-dom";
import { motion, AnimatePresence } from "framer-motion";
import { useUIState } from "../../bridges/uiStateContext";
import { SingleButton, TwinColumn, SplitRowHalf, SplitRowQuarter } from "./ButtonGroup";

const Backdrop = ({ onClick }) => (
  <motion.div
    className="fixed inset-0 bg-black bg-opacity-50 z-40"
    initial={{ opacity: 0 }}
    animate={{ opacity: 1 }}
    exit={{ opacity: 0 }}
    onClick={onClick}
  />
);

const BaseBottomSheet = ({
  isOpen,
  onClose,
  children,
  height,
  topOffset = "",
}) => {
  const { registerBottomSheetCloser } = useUIState();
  const unregisterRef = useRef(null);

  const handleBackClose = useCallback(() => {
    onClose?.();
  }, [onClose]);

  useLayoutEffect(() => {
    if (!isOpen) return;
    const unregister = registerBottomSheetCloser(handleBackClose);
    unregisterRef.current = unregister;
    return () => unregisterRef.current?.();
  }, [isOpen, registerBottomSheetCloser, handleBackClose]);

  // height가 없고 topOffset 있을 때만 maxHeight 적용
  const style =
    !height && topOffset
      ? { maxHeight: `calc(100vh - ${topOffset})` }
      : undefined;

  const sheet = (
    <AnimatePresence>
      {isOpen && (
        <>
          <Backdrop onClick={onClose} />
          <motion.div
            className={`
              fixed bottom-0 left-0 right-0
              bg-surface-light-white z-50
              rounded-t-[30px]
              flex flex-col
              overflow-hidden
              ${height || ""} 
            `}
            style={style}
            initial={{ y: "100%" }}
            animate={{ y: 0 }}
            exit={{ y: "100%" }}
            transition={{ duration: 0.3 }}
          >
            <div className="h-full pt-[20px] flex flex-col min-h-0">
              {children}
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );

  return ReactDOM.createPortal(sheet, document.body);
};


/**
 * 타입 1: 상단 디자인 (제목 + 콘텐츠 영역만)
 *  - 이미지 상단 예시
 */

// ButtonGroup 매핑
const BUTTON_COMPONENTS = {
  single: SingleButton,
  columm: TwinColumn,
  half: SplitRowHalf,
  quarter: SplitRowQuarter,
};


export const Bottom = ({
  isOpen,
  onClose,
  title = "제목을 입력해주세요",
  children, // 점선 박스 등 원하는 영역
  height,
}) => (
  <BaseBottomSheet isOpen={isOpen} onClose={onClose} height={height}>
    <div className="flex flex-col h-full px-[20px]">
      <h2 className="text-t2 text-text-default pb-[24px]">
        {title}
      </h2>

      {/* 점선 영역 등을 children으로 */}
      <div className="flex-1 pb-[8px]">{children}</div>
    </div>
  </BaseBottomSheet>
);

/**
 * 타입 2: 하단 디자인 (제목 + 설명 텍스트 + 콘텐츠 + 하단 버튼)
 *  - 이미지 하단 예시
 */
export const BottomWithButton = ({
  isOpen,
  onClose,
  title = "제목을 입력해주세요",
  subtitle = "설명을 입력해주세요",
  children,
  buttonLabel = "버튼명",
  onSubmit,
  height,
  topOffset,
  buttonDisabled = false,
  buttonVariant = "fill",
  renderButton,
}) => {
  const handleClick = () => {
    if (buttonDisabled) return;
    onSubmit?.();
  };

  // 1순위: renderButton
  if (renderButton) {
    return (
      <BaseBottomSheet
        isOpen={isOpen}
        onClose={onClose}
        height={height}
        topOffset={topOffset}
      >
        <div className="flex flex-col h-full min-h-0">
          {/* 헤더 고정 */}
          <div className="sticky top-0">
            <h2 className="text-t2 text-text-default px-[20px] whitespace-pre-line">
              {title}
            </h2>
            <p className="text-b3 text-text-subtler pt-[8px] px-[20px] pb-[24px] whitespace-pre-line">
              {subtitle}
            </p>
          </div>

          {/* 가운데 내용만 스크롤 */}
          <div className="flex-1 min-h-0 px-[20px] overflow-y-auto">
            {children}
          </div>

          {/* 하단 버튼 고정 */}
          <div className="sticky bottom-0">
            {renderButton({ onClick: handleClick, disabled: buttonDisabled })}
          </div>
        </div>
      </BaseBottomSheet>
    );
  }

  // 2순위: buttonVariant 기반 ButtonGroup 버튼 사용
  const ButtonComp = BUTTON_COMPONENTS[buttonVariant] || SingleButton;

  return (
    <BaseBottomSheet
      isOpen={isOpen}
      onClose={onClose}
      height={height}
      topOffset={topOffset}
    >
      <div className="flex flex-col h-full min-h-0">
        {/* 헤더 고정 */}
        <div className="sticky top-0 pb-[24px]">
          <h2 className="text-t2 text-text-default px-[20px]">
            {title}
          </h2>
          {subtitle && (
            <p className="text-b3 text-text-subtler px-[20px] pt-[8px]">
              {subtitle}
            </p>
          )}
        </div>

        <div className="flex-1 min-h-0 px-[20px] overflow-y-auto">
          {children}
        </div>

        {/* 하단 버튼 고정 */}
        <div className="sticky bottom-0">
          <ButtonComp
            onClick={handleClick}
            disabled={buttonDisabled}
            fullWidth
          >
            {buttonLabel}
          </ButtonComp>
        </div>
      </div>
    </BaseBottomSheet>
  );
};


export default BaseBottomSheet;
